import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ArrowRight, Check, Download, Eye, FileText, ImagePlus, Loader2, Minus, RotateCcw, Send, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PdfPreview } from "@/components/PdfPreview";
import { useLanguage } from "@/components/language-provider";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AssistantActionResponse, AssistantAttachment, AssistantHttpError, AssistantImage, AssistantMessage, fetchAssistantFile,
  fetchAssistantImageUrl, fetchAssistantStatus, forgetAssistantConversation, loadAssistantConversation, runAssistantAction,
  saveFile, sendAssistantMessage, uploadAssistantImage, uploadForAssistantAction,
} from "@/lib/assistant/api";
import { Mascot, MascotMood, mascotFrameUrl, preloadMascot } from "./mascot";
import { assistantTexts } from "./texts";
import { MascotGuide } from "./mascot-guide";
import { ASSISTANT_ASK_EVENT, GUIDE_STATE_EVENT, MASCOT_LANDED_EVENT, guideForPath, requestGuide } from "./guide";
import "./assistant.css";

const UI_KEY = "fs:assistant:ui";
const CONV_KEY = "fs:assistant:conv";
const SLOW_AFTER_MS = 7000;
const SLEEPY_AFTER_MS = 120_000;
const GREETING = /^\s*(oi+|ol[aá]|e a[ií]|bom dia|boa tarde|boa noite|hi+|hello|hey|yo)\b/i;

type UiState = "closed" | "open" | "min";

interface ChatLine extends AssistantMessage {
  key: string;
  outcome?: string;
}

/** A screenshot chosen for the next message: local preview while it uploads, then its server id. */
interface Draft {
  localUrl: string;
  name: string;
  id?: string;
  failed?: boolean;
}

const PUBLIC_ROUTES = /^\/(login|register|forgot-password|reset-password|complete-registration|auth\/|sign\/|verificar|verify|verify-document|verify-phone)/;

/** Mounts the chat on every signed-in page (the wizard and the sign page included), never on public ones. */
export function AssistantHost() {
  const [location] = useLocation();
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      const raw = localStorage.getItem("user");
      setUser(token && raw ? JSON.parse(raw) : null);
    } catch {
      setUser(null);
    }
  }, [location]);
  if (!user || PUBLIC_ROUTES.test(location)) return null;
  return (
    <>
      <AssistantWidget user={user} />
      <MascotGuide />
    </>
  );
}

/** Floating chat with the FillSign mascot. */
export function AssistantWidget({ user }: { user: any }) {
  const { language } = useLanguage();
  const t = useMemo(() => assistantTexts(language), [language]);
  const isMobile = useIsMobile();
  const [, navigate] = useLocation();

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [ui, setUi] = useState<UiState>(() => (sessionStorage.getItem(UI_KEY) as UiState) || "closed");
  const [conversationId, setConversationId] = useState<string | null>(() => sessionStorage.getItem(CONV_KEY));
  const [lines, setLines] = useState<ChatLine[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [slow, setSlow] = useState(false);
  const [mood, setMood] = useState<MascotMood>("idle");
  const [speech, setSpeech] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ file: File; title: string } | null>(null);
  const [busyFile, setBusyFile] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  /** Private image urls resolved with the token (user bubbles of restored conversations). */
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  /** State of action cards by their url: done | dismissed | failed | busy */
  const [actions, setActions] = useState<Record<string, string>>({});
  const imageInputRef = useRef<HTMLInputElement>(null);
  /** The mascot is out on the page pointing at something (its seat in the header stays empty). */
  const [away, setAway] = useState(false);
  const [landed, setLanded] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const moodTimer = useRef<number | undefined>(undefined);
  const speechTimer = useRef<number | undefined>(undefined);
  const typingTimer = useRef<number | undefined>(undefined);
  const sleepTimer = useRef<number | undefined>(undefined);
  const files = useRef(new Map<string, File>());
  const pendingRef = useRef(false);
  /** Enter pressed while a screenshot is still uploading: the message goes out as soon as the upload ends. */
  const queuedSend = useRef<string | null>(null);

  // ── Mood helpers ──────────────────────────────────────────────────────────────────────────────
  const say = useCallback((text: string | null, ms = 2800) => {
    window.clearTimeout(speechTimer.current);
    setSpeech(text);
    if (text) speechTimer.current = window.setTimeout(() => setSpeech(null), ms);
  }, []);

  /** Shows a mood for a while, then goes back to idle (unless a reply is being awaited). */
  const feel = useCallback((m: MascotMood, ms?: number) => {
    window.clearTimeout(moodTimer.current);
    setMood(m);
    if (ms) moodTimer.current = window.setTimeout(() => setMood(pendingRef.current ? "thinking" : "idle"), ms);
  }, []);

  const resetSleep = useCallback(() => {
    window.clearTimeout(sleepTimer.current);
    sleepTimer.current = window.setTimeout(() => {
      if (!pendingRef.current) { feel("sleepy"); say(t.sleepy, 4000); }
    }, SLEEPY_AFTER_MS);
  }, [feel, say, t]);

  // ── Setup ─────────────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    fetchAssistantStatus().then((s) => { if (!cancelled) setEnabled(s ? s.enabled : false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { sessionStorage.setItem(UI_KEY, ui); }, [ui]);
  useEffect(() => {
    if (conversationId) sessionStorage.setItem(CONV_KEY, conversationId);
    else sessionStorage.removeItem(CONV_KEY);
  }, [conversationId]);

  // Restore the conversation the server still holds (same session, after a page change or reload).
  useEffect(() => {
    if (!enabled || !conversationId || lines.length > 0) return;
    let cancelled = false;
    loadAssistantConversation(conversationId).then((c) => {
      if (cancelled) return;
      if (!c) { setConversationId(null); return; }
      setLines(c.messages.map((m, i) => ({ ...m, key: `r${i}` })));
    }).catch(() => { /* starts fresh */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, conversationId]);

  useEffect(() => {
    if (ui !== "open") return;
    preloadMascot();
    feel("wave", 2200);
    say(t.greet, 1800);
    resetSleep();
    const focus = window.setTimeout(() => inputRef.current?.focus(), 250);
    return () => { window.clearTimeout(focus); window.clearTimeout(sleepTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, pending, ui]);

  useEffect(() => () => {
    window.clearTimeout(moodTimer.current);
    window.clearTimeout(speechTimer.current);
    window.clearTimeout(typingTimer.current);
    window.clearTimeout(sleepTimer.current);
  }, []);

  useEffect(() => {
    const onState = (e: Event) => {
      const d = (e as CustomEvent<{ active: boolean; rect?: DOMRect }>).detail;
      setAway(!!d?.active);
      if (d?.active && d.rect) {
        // The page must be visible while the mascot points: full-screen on mobile, or when the panel covers the target.
        const panel = panelRef.current?.getBoundingClientRect();
        const overlaps = panel && !(d.rect.right < panel.left || d.rect.left > panel.right || d.rect.bottom < panel.top || d.rect.top > panel.bottom);
        if (isMobile || overlaps) setUi("min");
      }
    };
    const onLanded = () => {
      setAway(false);
      setLanded(true);
      feel("happy", 1800);
      window.setTimeout(() => setLanded(false), 1900);
    };
    window.addEventListener(GUIDE_STATE_EVENT, onState);
    window.addEventListener(MASCOT_LANDED_EVENT, onLanded);
    return () => {
      window.removeEventListener(GUIDE_STATE_EVENT, onState);
      window.removeEventListener(MASCOT_LANDED_EVENT, onLanded);
    };
  }, [isMobile, feel]);

  // A page button asked the assistant something: open the chat and send it as the person's line.
  const sendRef = useRef<(text: string) => Promise<void>>(async () => {});
  useEffect(() => {
    const onAsk = (e: Event) => {
      const message = (e as CustomEvent<{ message: string }>).detail?.message;
      if (!message) return;
      setUi("open");
      window.setTimeout(() => { void sendRef.current(message); }, 250);
    };
    window.addEventListener(ASSISTANT_ASK_EVENT, onAsk);
    return () => window.removeEventListener(ASSISTANT_ASK_EVENT, onAsk);
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────────────────────────
  const onType = (value: string) => {
    setInput(value);
    resetSleep();
    if (mood === "sleepy") { feel("surprised", 900); say(t.wake, 1500); }
    else if (!pending) {
      if (value.trim()) { if (mood !== "writing") { setMood("writing"); say(t.typingAlong, 1600); } }
      else setMood("idle");
      window.clearTimeout(typingTimer.current);
      typingTimer.current = window.setTimeout(() => { if (!pendingRef.current) setMood("idle"); }, 1500);
    }
  };

  const send = async (text: string) => {
    const message = text.trim();
    const ready = drafts.filter((d) => d.id && !d.failed);
    if ((!message && ready.length === 0) || pending) return;
    if (drafts.some((d) => !d.id && !d.failed)) { queuedSend.current = message; return; }
    setInput("");
    setDrafts([]);
    window.clearTimeout(typingTimer.current);
    const images: AssistantImage[] = ready.map((d) => ({ id: d.id!, fileName: d.name, url: `/api/assistant/uploads/${d.id}` }));
    setImageUrls((u) => ({ ...u, ...Object.fromEntries(ready.map((d) => [d.id!, d.localUrl])) }));
    const userLine: ChatLine = { key: `u${Date.now()}`, role: "user", text: message, at: new Date().toISOString(), attachments: [], images };
    setLines((l) => [...l, userLine]);
    setPending(true);
    pendingRef.current = true;
    setSlow(false);
    resetSleep();

    const shouting = message.length >= 6 && message === message.toUpperCase() && /[A-ZÀ-Ú]/.test(message);
    if (GREETING.test(message)) { feel("wave"); say(null); window.setTimeout(() => { if (pendingRef.current) setMood("thinking"); }, 1200); }
    else if (shouting || (message.match(/!/g) || []).length >= 3) { feel("surprised"); window.setTimeout(() => { if (pendingRef.current) setMood("thinking"); }, 1000); }
    else { feel("thinking"); say(t.thinking, 1800); }

    const slowTimer = window.setTimeout(() => { setSlow(true); feel("sorry"); say(t.slow, 3500); }, SLOW_AFTER_MS);
    try {
      const turn = await sendAssistantMessage(message, conversationId, language, images.map((i) => i.id));
      setConversationId(turn.conversationId);
      setLines((l) => [...l, { ...turn.reply, key: `a${Date.now()}`, outcome: turn.outcome }]);
      feel(replyMood(turn.reply.mood, turn.outcome), 4500);
      if (turn.outcome === "handoff") say(t.handoff, 4000);
      const page = turn.reply.attachments.find((a) => a.kind === "page");
      if (page) {
        const guide = guideForPath(page.url, page.title);
        if (guide) window.setTimeout(() => requestGuide(guide), 600);
      }
    } catch (err) {
      const status = err instanceof AssistantHttpError ? err.status : 0;
      const text = status === 401 ? t.sessionExpired : status === 429 ? t.tooMany : t.offline;
      setLines((l) => [...l, { key: `e${Date.now()}`, role: "assistant", text, at: new Date().toISOString(), mood: "sad", attachments: [], outcome: "error" }]);
      feel("sad", 4500);
    } finally {
      window.clearTimeout(slowTimer);
      setPending(false);
      pendingRef.current = false;
      setSlow(false);
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const newConversation = async () => {
    if (conversationId) void forgetAssistantConversation(conversationId);
    setConversationId(null);
    setLines([]);
    files.current.clear();
    feel("wave", 1800);
    inputRef.current?.focus();
  };

  const getFile = async (a: AssistantAttachment) => {
    const cached = files.current.get(a.url);
    if (cached) return cached;
    setBusyFile(a.url);
    try {
      const file = await fetchAssistantFile(a);
      files.current.set(a.url, file);
      return file;
    } finally {
      setBusyFile(null);
    }
  };

  const openAttachment = async (a: AssistantAttachment, action: "view" | "download") => {
    resetSleep();
    try {
      const file = await getFile(a);
      if (action === "view") setPreview({ file, title: a.title });
      else saveFile(file);
      feel("happy", 2000);
    } catch {
      feel("sorry", 3000);
      say(t.fileError, 3000);
    }
  };

  const openPage = (a: AssistantAttachment) => {
    navigate(a.url);
    if (isMobile) setUi("min");
  };

  // Uploads finished after an Enter: send the queued message now.
  useEffect(() => {
    if (queuedSend.current === null || drafts.some((d) => !d.id && !d.failed)) return;
    const message = queuedSend.current;
    queuedSend.current = null;
    void send(message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts]);

  /** Screenshots from the paperclip, a paste or a drop: uploaded right away, sent with the next message. */
  const attachImages = async (list: FileList | File[] | null) => {
    if (!list) return;
    const picked = Array.from(list).filter((f) => f.type.startsWith("image/")).slice(0, 4 - drafts.length);
    if (picked.length === 0) { say(t.imageOnly, 2500); return; }
    resetSleep();
    for (const file of picked) {
      if (file.size > 4 * 1024 * 1024) { say(t.imageOnly, 2500); continue; }
      const draft: Draft = { localUrl: URL.createObjectURL(file), name: file.name || "print.png" };
      setDrafts((d) => [...d, draft]);
      try {
        const up = await uploadAssistantImage(file);
        setDrafts((d) => d.map((x) => (x === draft ? { ...x, id: up.id, name: up.fileName } : x)));
      } catch {
        setDrafts((d) => d.map((x) => (x === draft ? { ...x, failed: true } : x)));
        say(t.uploadError, 3000);
      }
    }
    feel("surprised", 900);
    inputRef.current?.focus();
  };

  /** Private images of restored conversations are fetched with the token once. */
  useEffect(() => {
    const missing = lines.flatMap((l) => l.images ?? []).filter((i) => !imageUrls[i.id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const img of missing) {
        try {
          const url = await fetchAssistantImageUrl(img);
          if (!cancelled) setImageUrls((u) => ({ ...u, [img.id]: url }));
        } catch { /* the thumbnail stays empty */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  const applyActionResponse = (a: AssistantAttachment, res: AssistantActionResponse) => {
    setActions((s) => ({ ...s, [a.url]: res.status === "done" ? "done" : res.status === "dismissed" ? "dismissed" : "failed" }));
    if (res.message) setLines((l) => [...l, { ...res.message!, key: `s${Date.now()}`, attachments: res.message!.attachments ?? [] }]);
    if (res.status === "done") feel("happy", 3000);
    else if (res.status !== "dismissed") { feel("sorry", 3000); if (res.status === "not_found" || res.status === "expired") say(t.actionExpired, 4000); }
  };

  const runAction = async (a: AssistantAttachment, verb: "confirm" | "dismiss") => {
    resetSleep();
    setActions((s) => ({ ...s, [a.url]: "busy" }));
    try {
      applyActionResponse(a, await runAssistantAction(a.url, verb));
    } catch {
      setActions((s) => ({ ...s, [a.url]: "failed" }));
      feel("sorry", 3000);
    }
  };

  const uploadAction = async (a: AssistantAttachment, file: File | undefined) => {
    if (!file) return;
    resetSleep();
    setActions((s) => ({ ...s, [a.url]: "busy" }));
    feel("thinking");
    try {
      applyActionResponse(a, await uploadForAssistantAction(a.url, file));
    } catch {
      setActions((s) => ({ ...s, [a.url]: "failed" }));
      feel("sorry", 3000);
    }
  };

  sendRef.current = send;
  const poke = () => {
    resetSleep();
    const moods: MascotMood[] = ["surprised", "happy", "wave"];
    feel(moods[Math.floor(Math.random() * moods.length)], 1600);
    say(t.poke[Math.floor(Math.random() * t.poke.length)], 1600);
  };

  if (enabled === false || enabled === null) return null;

  const firstName = user?.firstName ? String(user.firstName).split(" ")[0] : "";
  const hello = firstName ? t.hello.replace("Oi!", `Oi, ${firstName}!`).replace("Hi!", `Hi, ${firstName}!`) : t.hello;

  // ── Launcher ─────────────────────────────────────────────────────────────────────────────────
  if (ui !== "open") {
    return (
      <button
        type="button"
        onClick={() => setUi("open")}
        className={`fs-assistant-launcher fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border border-primary/60 bg-card pl-1 pr-3 py-1 shadow-lg hover:shadow-xl transition-shadow ${landed ? "fs-landed" : ""}`}
        aria-label={t.open}
        data-testid="assistant-launcher"
      >
        <img src={mascotFrameUrl(away ? "idle_b" : "writing")} alt="" className={`h-11 w-11 ${away ? "opacity-30" : ""}`} draggable={false} data-guide-home />
        <span className="text-sm font-medium hidden sm:inline">{ui === "min" && lines.length > 0 ? t.name : t.open}</span>
      </button>
    );
  }

  // ── Panel ────────────────────────────────────────────────────────────────────────────────────
  const panelClass = isMobile
    ? "fixed inset-0 z-50 flex flex-col bg-card"
    : "fs-assistant-panel fixed bottom-4 right-4 z-50 flex w-[380px] max-w-[calc(100vw-2rem)] flex-col rounded-2xl border bg-card shadow-2xl";
  const panelStyle = isMobile ? undefined : { height: "min(600px, calc(100vh - 2rem))" };

  return (
    <>
      <div ref={panelRef} className={panelClass} style={panelStyle} role="dialog" aria-label={t.name} data-testid="assistant-panel">
        <header className="flex items-center gap-3 border-b px-3 py-2">
          {away ? (
            <span className="inline-block" style={{ width: 52, height: 52 }} data-guide-home data-testid="assistant-mascot-away">
              <img src={mascotFrameUrl("idle_a")} alt="" className="h-full w-full opacity-20" draggable={false} />
            </span>
          ) : (
            <span data-guide-home className="inline-block"><Mascot mood={mood} size={52} onClick={poke} title={t.name} /></span>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight">{t.name}</p>
            {speech ? (
              <p className="fs-assistant-speech inline-block max-w-full truncate rounded-xl rounded-tl-sm bg-primary px-2 py-0.5 text-xs text-primary-foreground" data-testid="assistant-speech">
                {speech}
              </p>
            ) : (
              <p className="truncate text-xs text-muted-foreground">{pending ? (slow ? t.slow : t.thinking) : t.tagline}</p>
            )}
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={newConversation} aria-label={t.newChat} title={t.newChat} data-testid="assistant-new">
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setUi("min")} aria-label={t.minimize} title={t.minimize} data-testid="assistant-minimize">
            <Minus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setUi("closed")} aria-label={t.close} title={t.close} data-testid="assistant-close">
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3" data-testid="assistant-messages">
          {lines.length === 0 && (
            <>
              <Bubble role="assistant" text={hello} />
              <div className="flex flex-wrap gap-2 pl-9" data-testid="assistant-suggestions">
                {t.suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)}
                    className="rounded-full border bg-background px-3 py-1 text-xs hover:bg-muted transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}
          {lines.map((m) => (
            <Bubble key={m.key} role={m.role} text={m.text} outcome={m.outcome}
              images={m.images?.map((i) => ({ id: i.id, name: i.fileName, url: imageUrls[i.id] }))}>
              {m.attachments.length > 0 && (
                <div className="mt-2 space-y-2">
                  {m.attachments.map((a) => a.kind === "action" ? (
                    <ActionCard key={a.url} a={a} state={actions[a.url]} t={t}
                      onConfirm={() => runAction(a, "confirm")} onDismiss={() => runAction(a, "dismiss")} onFile={(f) => uploadAction(a, f)} />
                  ) : a.kind === "file" ? (
                    <div key={a.url} className="flex items-center gap-2 rounded-lg border bg-background p-2" data-testid="assistant-file-card">
                      <FileText className="h-5 w-5 shrink-0 text-secondary" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium" title={a.title}>{a.title}</p>
                        {a.subtitle && <p className="text-xs text-muted-foreground">{a.subtitle}</p>}
                      </div>
                      <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => openAttachment(a, "view")} disabled={busyFile === a.url} title={t.view} data-testid="assistant-file-view">
                        {busyFile === a.url ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => openAttachment(a, "download")} disabled={busyFile === a.url} title={t.download} data-testid="assistant-file-download">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button key={a.url} variant="secondary" size="sm" className="h-auto max-w-full gap-1 whitespace-normal py-1.5 text-left" onClick={() => openPage(a)} data-testid="assistant-page-button">
                      <span className="min-w-0">{a.title}</span> <ArrowRight className="h-4 w-4 shrink-0" />
                    </Button>
                  ))}
                </div>
              )}
            </Bubble>
          ))}
          {pending && (
            <div className="flex items-end gap-2">
              <Mascot mood="thinking" size={28} />
              <div className="fs-assistant-typing rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-foreground" data-testid="assistant-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
        </div>

        <form
          className="border-t p-2"
          onSubmit={(e) => { e.preventDefault(); void send(input); }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); void attachImages(e.dataTransfer.files); }}
        >
          {drafts.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2" data-testid="assistant-drafts">
              {drafts.map((d) => (
                <span key={d.localUrl} className={`relative inline-block h-14 w-14 overflow-hidden rounded-lg border ${d.failed ? "border-destructive" : ""}`}>
                  <img src={d.localUrl} alt={d.name} className="h-full w-full object-cover" />
                  {!d.id && !d.failed && <span className="absolute inset-0 flex items-center justify-center bg-background/60"><Loader2 className="h-4 w-4 animate-spin" /></span>}
                  <button type="button" onClick={() => setDrafts((x) => x.filter((y) => y !== d))} aria-label={t.close}
                    className="absolute right-0 top-0 rounded-bl-lg bg-background/90 p-0.5"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { void attachImages(e.target.files); e.target.value = ""; }} data-testid="assistant-image-input" />
            <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0 rounded-xl" onClick={() => imageInputRef.current?.click()} aria-label={t.attach} title={t.attach} data-testid="assistant-attach">
              <ImagePlus className="h-4 w-4" />
            </Button>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => onType(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(input); } }}
              onPaste={(e) => { const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith("image/")); if (files.length) { e.preventDefault(); void attachImages(files); } }}
              onFocus={resetSleep}
              rows={1}
              maxLength={2000}
              placeholder={t.placeholder}
              className="max-h-28 min-h-[40px] flex-1 resize-none rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              data-testid="assistant-input"
            />
            <Button type="submit" size="icon" className="h-10 w-10 shrink-0 rounded-xl" disabled={pending || (!input.trim() && !drafts.some((d) => d.id)) || drafts.some((d) => !d.id && !d.failed)} aria-label={t.send} data-testid="assistant-send">
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl" data-testid="assistant-preview">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">{preview?.title ?? t.previewTitle}</DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <PdfPreview src={preview.file} maxHeight={Math.max(320, window.innerHeight - 260)} />
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => saveFile(preview.file)}>
                  <Download className="mr-2 h-4 w-4" /> {t.download}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function replyMood(mood: string | null | undefined, outcome: string): MascotMood {
  if (outcome === "limit") return "sleepy";
  if (outcome === "unavailable" || outcome === "disabled") return "sad";
  if (outcome === "offtopic") return "neutral";
  switch (mood) {
    case "happy": case "sorry": case "sad": case "sleepy": case "thinking": case "wave": case "surprised":
      return mood;
    default:
      return "idle";
  }
}

function Bubble({ role, text, outcome, images, children }: {
  role: "user" | "assistant"; text: string; outcome?: string; images?: { id: string; name: string; url?: string }[]; children?: React.ReactNode;
}) {
  if (role === "user") {
    return (
      <div className="fs-assistant-bubble flex justify-end" data-testid="assistant-line-user">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
          {images && images.length > 0 && (
            <div className="mb-1 flex flex-wrap gap-1" data-testid="assistant-line-images">
              {images.map((i) => i.url
                ? <a key={i.id} href={i.url} target="_blank" rel="noopener noreferrer"><img src={i.url} alt={i.name} className="h-24 max-w-[160px] rounded-lg border border-primary-foreground/30 object-cover" /></a>
                : <span key={i.id} className="flex h-24 w-24 items-center justify-center rounded-lg border border-primary-foreground/30 text-xs">{i.name}</span>)}
            </div>
          )}
          {text && <span className="whitespace-pre-wrap break-words">{text}</span>}
        </div>
      </div>
    );
  }
  const tone = outcome === "error" || outcome === "unavailable" ? "border-destructive/40" : "";
  return (
    <div className="fs-assistant-bubble flex items-end gap-2" data-testid="assistant-line-assistant">
      <img src={mascotFrameUrl("idle_a")} alt="" className="h-7 w-7 shrink-0" draggable={false} />
      <div className={`max-w-[85%] rounded-2xl rounded-bl-sm border bg-muted px-3 py-2 text-sm ${tone}`}>
        <FormattedText text={text} />
        {children}
      </div>
    </div>
  );
}

/** Light formatting of the assistant's text: paragraphs, "- " bullets, *bold* and plain links. */
function FormattedText({ text }: { text: string }) {
  const blocks = useMemo(() => {
    const out: { type: "p" | "ul"; lines: string[] }[] = [];
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trimEnd();
      const bullet = /^\s*(?:[-•*]|\d+[.)])\s+(.*)$/.exec(line);
      if (bullet) {
        if (out[out.length - 1]?.type === "ul") out[out.length - 1].lines.push(bullet[1]);
        else out.push({ type: "ul", lines: [bullet[1]] });
      } else if (line.trim()) {
        out.push({ type: "p", lines: [line] });
      }
    }
    return out;
  }, [text]);

  return (
    <div className="space-y-1.5">
      {blocks.map((b, i) => b.type === "ul" ? (
        <ul key={i} className="list-disc space-y-0.5 pl-4">
          {b.lines.map((l, j) => <li key={j}><Inline text={l} /></li>)}
        </ul>
      ) : (
        <p key={i} className="break-words"><Inline text={b.lines[0]} /></p>
      ))}
    </div>
  );
}

function Inline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|https?:\/\/[^\s)]+)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (!p) return null;
        if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (/^\*[^*\s][^*]*\*$/.test(p)) return <strong key={i}>{p.slice(1, -1)}</strong>;
        if (/^https?:\/\//.test(p)) return <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="underline break-all">{p}</a>;
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

/** A change the assistant proposed: nothing happens until the person presses Confirm (or picks the new file). */
function ActionCard({ a, state, t, onConfirm, onDismiss, onFile }: {
  a: AssistantAttachment; state?: string; t: ReturnType<typeof assistantTexts>;
  onConfirm: () => void; onDismiss: () => void; onFile: (f: File | undefined) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const settled = state === "done" || state === "dismissed" || state === "failed";
  const busy = state === "busy";
  return (
    <div className={`rounded-lg border p-2 ${state === "done" ? "border-emerald-500/60 bg-emerald-50/60 dark:bg-emerald-950/30" : state === "failed" ? "border-destructive/50" : "border-primary/60 bg-background"}`} data-testid="assistant-action-card" data-action={a.action} data-state={state ?? "pending"}>
      <p className="text-sm font-semibold">{a.subtitle}</p>
      <p className="truncate text-sm" title={a.title}>{a.title}</p>
      {a.details && a.details.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
          {a.details.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {settled ? (
          <span className={`inline-flex items-center gap-1 text-xs font-medium ${state === "done" ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}>
            {state === "done" && <Check className="h-3.5 w-3.5" />}
            {state === "done" ? t.actionDone : state === "dismissed" ? t.actionDismissed : t.actionFailed}
          </span>
        ) : a.action === "replace_document" ? (
          <>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,application/pdf" className="hidden" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="assistant-action-file" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="assistant-action-choose">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} {t.chooseFile}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss} disabled={busy} data-testid="assistant-action-dismiss">{t.dismiss}</Button>
          </>
        ) : (
          <>
            <Button size="sm" onClick={onConfirm} disabled={busy} data-testid="assistant-action-confirm">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />} {t.confirm}
            </Button>
            <Button size="sm" variant="outline" onClick={onDismiss} disabled={busy} data-testid="assistant-action-dismiss">{t.dismiss}</Button>
          </>
        )}
      </div>
    </div>
  );
}
